"use client";

/**
 * useTimezoneSync Hook
 *
 * Automatically detects user's browser timezone and syncs to backend.
 * Runs once per session - caches in localStorage to avoid redundant API calls.
 */

import { useEffect } from "react";
import { useAuth } from "@clerk/nextjs";

const TIMEZONE_CACHE_KEY = "hog_user_timezone";

export function useTimezoneSync() {
  const { isSignedIn } = useAuth();

  useEffect(() => {
    if (!isSignedIn) return;

    const syncTimezone = async () => {
      try {
        // Detect browser timezone
        const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        if (!detectedTimezone) return;

        // Check if already synced this session
        const cachedTimezone = localStorage.getItem(TIMEZONE_CACHE_KEY);

        if (cachedTimezone === detectedTimezone) {
          return; // Already synced, skip API call
        }

        // Sync to backend
        const response = await fetch("/api/user/timezone", {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ timezone: detectedTimezone }),
        });

        if (response.ok) {
          // Cache to avoid redundant calls
          localStorage.setItem(TIMEZONE_CACHE_KEY, detectedTimezone);
        }
      } catch (error) {
        // Silent fail - timezone sync is non-critical
        console.error("[useTimezoneSync] Failed to sync timezone:", error);
      }
    };

    syncTimezone();
  }, [isSignedIn]);
}
